import { ReportExecutionHistory } from "./ReportExecutionHistory";
import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Dialog,
  Field,
  Grid,
  Inline,
  Panel,
  SelectField,
  Stack,
  StatusBadge,
  TextAreaField,
  useToast
} from "@leylion/design-system";
import {
  createReportExport,
  downloadReportExport,
  getReportDefinitions,
  getReportSchedules,
  runReport,
  runReportSchedule,
  saveReportDefinition,
  saveReportSchedule
} from "../api/client";
import type {
  ReportDataset,
  ReportDefinition,
  ReportExportFormat,
  ReportRun,
  ReportSchedule,
  ReportScheduleFrequency,
  ReportScope
} from "../api/reports-types";
import {
  DynamicReportTable,
  ReportError,
  dateTimeLabel,
  statusTone,
  type ReportsLanguage
} from "./reports-components";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected error.";
}

const datasetOptions: ReportDataset[] = [
  "Executive",
  "Sales",
  "Profitability",
  "Products",
  "Inventory",
  "Purchasing",
  "Customers",
  "Employees",
  "Restaurant",
  "TaxVat",
  "CashPayments",
  "BranchComparison",
  "Forecasting",
  "GeneralLedger",
  "TrialBalance",
  "ProfitAndLoss",
  "BalanceSheet"
];

const datasetFields: Record<
  ReportDataset,
  { dimensions: string[]; measures: string[] }
> = {
  Executive: {
    dimensions: ["date", "branch"],
    measures: ["netSales", "netProfit", "averageOrder", "grossMargin"]
  },
  Sales: {
    dimensions: ["date", "branch", "serviceType", "paymentMethod"],
    measures: ["orders", "grossMinor", "discountMinor", "refundMinor", "netMinor"]
  },
  Profitability: {
    dimensions: ["date", "category", "branch"],
    measures: ["netSales", "cogs", "grossProfit", "expenses", "netProfit", "margin"]
  },
  Products: {
    dimensions: ["product", "variant", "sku", "category"],
    measures: ["quantity", "sales", "cost", "profit", "margin", "available"]
  },
  Inventory: {
    dimensions: ["warehouse", "product", "variant", "sku"],
    measures: ["onHand", "reserved", "available", "value", "coverageDays"]
  },
  Purchasing: {
    dimensions: ["supplier", "purchaseOrder", "date"],
    measures: ["spend", "orders", "onTime"]
  },
  Customers: {
    dimensions: ["segment", "tier", "date"],
    measures: ["customers", "revenue", "retention", "clv"]
  },
  Employees: {
    dimensions: ["employee", "branch"],
    measures: ["sales", "orders", "aov", "salesPerHour", "refunds"]
  },
  Restaurant: {
    dimensions: ["station", "servicePeriod", "driver"],
    measures: ["sales", "tableTurnover", "kitchenTime", "waste", "onTime"]
  },
  TaxVat: {
    dimensions: ["taxCode", "date"],
    measures: ["net", "tax", "inputTax", "payable"]
  },
  CashPayments: {
    dimensions: ["paymentMethod", "shift", "cashier"],
    measures: ["receipts", "refunds", "variance"]
  },
  BranchComparison: {
    dimensions: ["branch"],
    measures: ["sales", "growth", "profit", "margin", "score"]
  },
  Forecasting: {
    dimensions: ["date", "scenario"],
    measures: ["actual", "forecast", "accuracy", "confidence"]
  },
  GeneralLedger: {
    dimensions: ["date", "account", "entry"],
    measures: ["debit", "credit", "balance"]
  },
  TrialBalance: {
    dimensions: ["account", "accountType"],
    measures: ["debit", "credit", "balance"]
  },
  ProfitAndLoss: {
    dimensions: ["account"],
    measures: ["current", "comparison"]
  },
  BalanceSheet: {
    dimensions: ["section", "account"],
    measures: ["current", "comparison"]
  }
};

export function ReportBuilderView({
  language,
  scope,
  refreshKey,
  canBuild,
  canSchedule,
  canExport,
  onScopeChange,
  onChanged
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
  canBuild: boolean;
  canSchedule: boolean;
  canExport: boolean;
  onChanged: () => void;
  onScopeChange: (scope: ReportScope) => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const [definitions, setDefinitions] = useState<ReportDefinition[]>([]);
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState("");
  const [dataset, setDataset] = useState<ReportDataset>("Sales");
  const [dimensions, setDimensions] = useState<string[]>([
    "date",
    "branch",
    "serviceType"
  ]);
  const [measures, setMeasures] = useState<string[]>([
    "orders",
    "grossMinor",
    "netMinor"
  ]);
  const [lastRun, setLastRun] = useState<ReportRun | null>(null);
  const runRequest = useRef(0);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [definitionCode, setDefinitionCode] = useState("");
  const [definitionNameAr, setDefinitionNameAr] = useState("");
  const [definitionNameEn, setDefinitionNameEn] = useState("");
  const [definitionDescription, setDefinitionDescription] = useState("");
  const [editingSchedule, setEditingSchedule] = useState<ReportSchedule | null>(null);
  const [scheduleZone, setScheduleZone] = useState("Asia/Dubai");
  const [schedulePeriod, setSchedulePeriod] = useState("PreviousComplete");
  const [scheduleConfirm, setScheduleConfirm] = useState(false);
  const [scheduleActive, setScheduleActive] = useState(true);
  const [historyKey, setHistoryKey] = useState(0);
  const [scheduleName, setScheduleName] = useState("");
  const [scheduleFrequency, setScheduleFrequency] =
    useState<ReportScheduleFrequency>("Daily");
  const [scheduleFormat, setScheduleFormat] =
    useState<ReportExportFormat>("Pdf");
  const [scheduleRecipients, setScheduleRecipients] = useState("");
  const [scheduleNextRun, setScheduleNextRun] = useState(
    new Date(Date.now() + 86_400_000).toISOString().slice(0, 16)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const [definitionRows, scheduleRows] = await Promise.all([
      getReportDefinitions(),
      canSchedule ? getReportSchedules() : Promise.resolve([])
    ]);
    setDefinitions(definitionRows);
    setSchedules(scheduleRows);
  };

  useEffect(() => {
    void load().catch((caught) => setError(errorMessage(caught)));
  }, [refreshKey, canSchedule]);

  useEffect(() => {
    runRequest.current++;
    setLastRun(null);
    setBusy(false);
  }, [scope, selectedDefinitionId, dataset]);

  useEffect(() => {
    const definition = definitions.find(item => item.id === selectedDefinitionId);
    const savedCurrency = definition?.filters.currency;
    // An explicit scope override becomes an ad-hoc run; it does not rewrite a saved definition.
    if (typeof savedCurrency === "string" && savedCurrency.trim().toUpperCase() !== scope.currency)
      setSelectedDefinitionId("");
  }, [scope.currency]);

  const setDatasetAndDefaults = (next: ReportDataset) => {
    setDataset(next);
    setDimensions(datasetFields[next].dimensions.slice(0, 3));
    setMeasures(datasetFields[next].measures.slice(0, 4));
    setSelectedDefinitionId("");
  };

  const selectDefinition = (id: string) => {
    setSelectedDefinitionId(id);
    const definition = definitions.find((item) => item.id === id);
    if (!definition) return;
    setDataset(definition.dataset);
    setDimensions(definition.dimensions);
    setMeasures(definition.measures);
    const currency = definition.filters.currency;
    if (typeof currency === "string" && currency.trim())
      onScopeChange({ ...scope, currency: currency.trim().toUpperCase() });
  };

  const execute = async () => {
    const requestId = ++runRequest.current;
    setBusy(true);
    setError("");
    try {
      const run = await runReport({
        reportDefinitionId: selectedDefinitionId || null,
        dataset,
        scope,
        dimensions,
        measures,
        filters: {},
        language
      });
      if (requestId !== runRequest.current) return;
      setLastRun({ ...run, currency: run.currency ?? scope.currency });
      toast.notify({
        tone: "success",
        title: ar ? "اكتمل التقرير" : "Report completed",
        message: `${run.table?.rows.length ?? 0} ${ar ? "صفًا" : "rows"}`
      });
    } catch (caught) {
      if (requestId === runRequest.current) setError(errorMessage(caught));
    } finally {
      if (requestId === runRequest.current) setBusy(false);
    }
  };

  const saveDefinition = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await saveReportDefinition({
        code: definitionCode,
        nameAr: definitionNameAr,
        nameEn: definitionNameEn,
        description: definitionDescription || null,
        dataset,
        status: "Active",
        dimensions,
        measures,
        filters: { currency: scope.currency },
        visualization: { type: "table" }
      });
      setBuilderOpen(false);
      setDefinitionCode("");
      setDefinitionNameAr("");
      setDefinitionNameEn("");
      setDefinitionDescription("");
      setSelectedDefinitionId(saved.id);
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم حفظ التقرير" : "Report definition saved"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = async () => {
    if (!selectedDefinitionId) return;
    setBusy(true);
    setError("");
    try {
      await saveReportSchedule({
        id: editingSchedule?.id,
        version: editingSchedule?.version,
        reportDefinitionId: selectedDefinitionId,
        name: scheduleName,
        frequency: scheduleFrequency,
        exportFormat: scheduleFormat,
        timeZone: scheduleZone,
        recipients: scheduleRecipients
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        parameters: {
          periodMode: schedulePeriod, language, currency: scope.currency,
          scope: schedulePeriod === "Fixed" ? scope : { branchIds: scope.branchIds, currency: scope.currency }
        },
        // The server converts this local wall-clock value in the explicit zone.
        localStart: scheduleNextRun,
        nextRunAt: editingSchedule?.nextRunAt ?? new Date(Date.now() + 86_400_000).toISOString(),
        confirmActivation: scheduleConfirm,
        isActive: scheduleActive
      });
      setScheduleOpen(false);
      setEditingSchedule(null);
      setScheduleConfirm(false);
      setHistoryKey(key => key + 1);
      setScheduleName("");
      setScheduleRecipients("");
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "تم حفظ إعداد الجدول" : "Schedule configuration saved"
      });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const runSchedule = async (schedule: ReportSchedule) => {
    const requestId = ++runRequest.current;
    setBusy(true);
    setError("");
    try {
      const run = await runReportSchedule(schedule);
      setHistoryKey(key => key + 1);
      if (requestId !== runRequest.current) return;
      setLastRun({ ...run, currency: run.currency ?? scope.currency });
      await load();
      onChanged();
      toast.notify({
        tone: "success",
        title: ar ? "اكتمل تشغيل الجدول" : "Schedule run completed"
      });
    } catch (caught) {
      if (requestId === runRequest.current) setError(errorMessage(caught));
    } finally {
      if (requestId === runRequest.current) setBusy(false);
    }
  };

  const exportRun = async (format: ReportExportFormat) => {
    if (!lastRun) return;
    setBusy(true);
    setError("");
    try {
      const exported = await createReportExport(lastRun.id, format);
      await downloadReportExport(exported.id, exported.fileName);
      toast.notify({
        tone: "success",
        title: ar ? "بدأ تنزيل الملف" : "File download started",
        message: exported.fileName
      });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      {error ? <ReportError language={language} error={error} /> : null}

      <Panel
        title={ar ? "منشئ التقارير" : "Report Builder"}
        description={
          ar
            ? "اختر مجموعة البيانات والأبعاد والمقاييس ثم شغّل التقرير أو احفظه."
            : "Select dataset, dimensions and measures, then run or save."
        }
        actions={
          <Inline>
            {canBuild ? (
              <Button
                variant="secondary"
                data-testid="save-report-definition"
                onClick={() => setBuilderOpen(true)}
              >
                {ar ? "حفظ تعريف" : "Save definition"}
              </Button>
            ) : null}
            {canSchedule && selectedDefinitionId ? (
              <Button
                variant="secondary"
                onClick={() => { setEditingSchedule(null); setScheduleConfirm(false); setScheduleActive(true); setScheduleOpen(true); }}
              >
                {ar ? "جدولة" : "Schedule"}
              </Button>
            ) : null}
            <Button
              data-testid="run-custom-report"
              icon="reports"
              disabled={busy || measures.length === 0}
              onClick={() => void execute()}
            >
              {busy
                ? ar ? "جارٍ التشغيل" : "Running"
                : ar ? "تشغيل التقرير" : "Run report"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Grid columns={2}>
            <SelectField
              label={ar ? "تقرير محفوظ" : "Saved report"}
              value={selectedDefinitionId}
              onChange={(event) => selectDefinition(event.target.value)}
            >
              <option value="">
                {ar ? "تقرير جديد / مخصص" : "New / ad hoc report"}
              </option>
              {definitions
                .filter((definition) => definition.status === "Active")
                .map((definition) => (
                  <option key={definition.id} value={definition.id}>
                    {ar ? definition.nameAr : definition.nameEn}
                    {" · "}
                    {definition.dataset}
                  </option>
                ))}
            </SelectField>
            <SelectField
              label={ar ? "مجموعة البيانات" : "Dataset"}
              value={dataset}
              onChange={(event) => setDatasetAndDefaults(
                event.target.value as ReportDataset
              )}
            >
              {datasetOptions.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </SelectField>
          </Grid>

          <div className="reports-builder-grid">
            <Card
              title={ar ? "الأبعاد" : "Dimensions"}
              description={
                ar
                  ? "تحدد مستوى التجميع."
                  : "Define the grouping grain."
              }
            >
              <div className="reports-builder-options">
                {datasetFields[dataset].dimensions.map((dimension) => (
                  <Checkbox
                    key={dimension}
                    label={dimension}
                    checked={dimensions.includes(dimension)}
                    onChange={(event) => {
                      setDimensions(event.target.checked
                        ? [...dimensions, dimension]
                        : dimensions.filter((item) => item !== dimension));
                    }}
                  />
                ))}
              </div>
            </Card>
            <Card
              title={ar ? "المقاييس" : "Measures"}
              description={
                ar
                  ? "إعداد محفوظ؛ يعرض التشغيل جدول البيانات القياسي حاليًا."
                  : "Saved configuration; execution currently returns the standard dataset table."
              }
            >
              <div className="reports-builder-options">
                {datasetFields[dataset].measures.map((measure) => (
                  <Checkbox
                    key={measure}
                    label={measure}
                    checked={measures.includes(measure)}
                    onChange={(event) => {
                      setMeasures(event.target.checked
                        ? [...measures, measure]
                        : measures.filter((item) => item !== measure));
                    }}
                  />
                ))}
              </div>
            </Card>
          </div>
        </Stack>
      </Panel>

      <Alert tone="info" title={ar ? "المعلمات المطبقة" : "Applied parameters"}>
        {ar
          ? "يطبق التشغيل نوع البيانات والتواريخ والفروع والعملة. اختيارات الأبعاد والمقاييس المخصصة محفوظة ولا تغيّر الجدول القياسي حاليًا. التنزيل يشمل جميع صفوف النتيجة، بحد 10,000 صف و20 MiB، ويظل متاحًا 30 يومًا."
          : "Execution applies the dataset, dates, branches and currency. Saved custom dimensions and measures do not currently change the standard table. Downloads include every result row, up to 10,000 rows and 20 MiB, and expire after 30 days."}
      </Alert>
      {lastRun?.table ? (
        <Stack>
          <Panel
            title={ar ? "نتيجة التقرير" : "Report result"}
            description={
              `${lastRun.dataset} · ${lastRun.table.rows.length} `
              + (ar ? "صفًا" : "rows") + ` · ${lastRun.currency ?? scope.currency}`
            }
            actions={
              canExport ? (
                <Inline>
                  {(["Csv", "Xlsx", "Pdf", "Json"] as ReportExportFormat[])
                    .map((format) => (
                      <Button
                        key={format}
                        size="sm"
                        variant="secondary"
                        icon="download"
                        disabled={busy}
                        onClick={() => void exportRun(format)}
                      >
                        {format}
                      </Button>
                    ))}
                </Inline>
              ) : null
            }
          >
            <StatusBadge tone={statusTone(lastRun.status)}>
              {lastRun.status}
            </StatusBadge>
          </Panel>
          <DynamicReportTable
            language={language}
            title={ar ? "بيانات التقرير" : "Report data"}
            table={lastRun.table}
            currency={lastRun.currency ?? scope.currency}
          />
        </Stack>
      ) : (
        <Alert
          tone="info"
          title={ar ? "لم يتم تشغيل تقرير بعد" : "No report run yet"}
        >
          {ar
            ? "حدد البيانات المطلوبة واضغط تشغيل التقرير."
            : "Choose the required fields and run the report."}
        </Alert>
      )}

      {canSchedule ? (
        <Panel title={ar ? "التقارير المجدولة" : "Scheduled reports"}>
          <div className="reports-schedule-grid">
            {schedules.length === 0 ? (
              <span>{ar ? "لا توجد جداول." : "No schedules."}</span>
            ) : schedules.map((schedule) => (
              <Card key={schedule.id} className="reports-schedule-card">
                <div className="reports-schedule-card__header">
                  <div>
                    <strong>{schedule.name}</strong>
                    <small>{schedule.reportName}</small>
                  </div>
                  <StatusBadge tone={schedule.activationStatus === "Active" ? "success" : "warning"}>
                    {schedule.activationStatus === "NeedsActivation" ? (ar ? "يحتاج مراجعة وتفعيلًا" : "Needs activation") : schedule.activationStatus}
                  </StatusBadge>
                </div>
                <dl>
                  <div>
                    <dt>{ar ? "التكرار" : "Frequency"}</dt>
                    <dd>{schedule.frequency}</dd>
                  </div>
                  <div>
                    <dt>{ar ? "التنسيق" : "Format"}</dt>
                    <dd>{schedule.exportFormat}</dd>
                  </div>
                  <div>
                    <dt>{ar ? "التشغيل التالي" : "Next run"}</dt>
                    <dd>{new Intl.DateTimeFormat(ar ? "ar-AE" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: schedule.timeZone }).format(new Date(schedule.nextRunAt))} — {schedule.timeZone}</dd>
                  </div>
                </dl>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => {
                  setEditingSchedule(schedule); setSelectedDefinitionId(schedule.reportDefinitionId);
                  setScheduleName(schedule.name); setScheduleZone(schedule.timeZone); setScheduleFrequency(schedule.frequency);
                  setScheduleFormat(schedule.exportFormat); setScheduleRecipients(schedule.recipients.join(", "));
                  setSchedulePeriod(String(schedule.parameters.periodMode ?? "PreviousComplete"));
                  const savedScope = schedule.parameters.scope as Partial<ReportScope> | undefined;
                  if (savedScope) onScopeChange({ ...scope, ...savedScope });
                  const parts = new Intl.DateTimeFormat("sv-SE", { timeZone: schedule.timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(schedule.nextRunAt));
                  setScheduleNextRun(parts.replace(" ", "T")); setScheduleActive(schedule.isActive);
                  setScheduleConfirm(false); setScheduleOpen(true);
                }}>{ar ? "مراجعة / تعديل / تفعيل" : "Review / edit / activate"}</Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy || !canExport || !schedule.isActive}
                  onClick={() => void runSchedule(schedule)}
                >
                  {ar ? "إنشاء ملف الآن (دون إرسال)" : "Generate now (no email)"}
                </Button>
              </Card>
            ))}
          </div>
        </Panel>
      ) : null}

      {canSchedule && <ReportExecutionHistory language={language} refreshKey={refreshKey + historyKey} canExport={canExport} />}

      <Dialog
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        title={ar ? "حفظ تعريف التقرير" : "Save report definition"}
        footer={
          <Inline>
            <Button
              variant="secondary"
              onClick={() => setBuilderOpen(false)}
            >
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              disabled={
                !definitionCode
                || !definitionNameAr
                || !definitionNameEn
                || measures.length === 0
              }
              onClick={() => void saveDefinition()}
            >
              {ar ? "حفظ" : "Save"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Grid columns={2}>
            <Field
              label={ar ? "الرمز" : "Code"}
              value={definitionCode}
              onChange={(event) => setDefinitionCode(event.target.value)}
            />
            <SelectField
              label={ar ? "مجموعة البيانات" : "Dataset"}
              value={dataset}
              onChange={(event) => setDatasetAndDefaults(
                event.target.value as ReportDataset
              )}
            >
              {datasetOptions.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </SelectField>
            <Field
              label={ar ? "الاسم العربي" : "Arabic name"}
              value={definitionNameAr}
              onChange={(event) => setDefinitionNameAr(event.target.value)}
            />
            <Field
              label={ar ? "الاسم الإنجليزي" : "English name"}
              value={definitionNameEn}
              onChange={(event) => setDefinitionNameEn(event.target.value)}
            />
          </Grid>
          <TextAreaField
            label={ar ? "الوصف" : "Description"}
            value={definitionDescription}
            onChange={(event) => setDefinitionDescription(event.target.value)}
          />
        </Stack>
      </Dialog>

      <Dialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        title={ar ? "جدولة التقرير" : "Schedule report"}
        footer={
          <Inline>
            <Button
              variant="secondary"
              onClick={() => setScheduleOpen(false)}
            >
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              disabled={
                busy || !scheduleName
                || (scheduleActive && !scheduleConfirm)
                || !scheduleRecipients
                || !scheduleNextRun
              }
              onClick={() => void saveSchedule()}
            >
              {ar ? "حفظ الجدول" : "Save schedule"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Grid columns={2}>
            <Field
              label={ar ? "اسم الجدول" : "Schedule name"}
              value={scheduleName}
              onChange={(event) => setScheduleName(event.target.value)}
            />
            <SelectField
              label={ar ? "التكرار" : "Frequency"}
              value={scheduleFrequency}
              onChange={(event) => setScheduleFrequency(
                event.target.value as ReportScheduleFrequency
              )}
            >
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
            </SelectField>
            <SelectField
              label={ar ? "التنسيق" : "Format"}
              value={scheduleFormat}
              onChange={(event) => setScheduleFormat(
                event.target.value as ReportExportFormat
              )}
            >
              <option value="Pdf">PDF</option>
              <option value="Xlsx">XLSX</option>
              <option value="Csv">CSV</option>
              <option value="Json">JSON</option>
            </SelectField>
            <Field label={ar ? "المنطقة الزمنية" : "Time zone"} value={scheduleZone}
              hint="Asia/Dubai, Asia/Amman, UTC, America/New_York"
              onChange={event => setScheduleZone(event.target.value)} />
            <SelectField label={ar ? "فترة البيانات" : "Data period"} value={schedulePeriod} onChange={event => setSchedulePeriod(event.target.value)}>
              <option value="PreviousComplete">{ar ? "الفترة التقويمية السابقة المكتملة" : "Previous complete calendar period"}</option>
              <option value="Fixed">{ar ? "نطاق الفلاتر الحالي الثابت" : "Fixed current filter range"}</option>
            </SelectField>
            <Field
              label={ar ? "الموعد المحلي في المنطقة المحددة" : "Local time in the selected zone"}
              type="datetime-local"
              value={scheduleNextRun}
              onChange={(event) => setScheduleNextRun(event.target.value)}
            />
          </Grid>
          <Field
            label={ar ? "المستلمون" : "Recipients"}
            hint={ar ? "مستخدمون فعالون داخل المؤسسة والفرع، لديهم صلاحية التقرير والتصدير. افصل العناوين بفاصلة." : "Active internal users in this branch with dataset and export access. Comma-separated emails."}
            value={scheduleRecipients}
            onChange={(event) => setScheduleRecipients(event.target.value)}
          />
          <Checkbox label={ar ? "الجدول فعال" : "Schedule active"} checked={scheduleActive} onChange={event => setScheduleActive(event.target.checked)} />
          {scheduleActive && <Checkbox checked={scheduleConfirm} onChange={event => setScheduleConfirm(event.target.checked)}
            label={ar ? "راجعت المستلمين والفروع والعملة والفترة وأؤكد التفعيل التلقائي" : "I reviewed recipients, branches, currency and period and confirm automatic activation"} />}
          <Alert tone="info" title={ar ? "سلوك الموعد" : "Schedule behavior"}>
            {ar ? "الأسبوع يبدأ الاثنين. يوم 31 يستخدم آخر يوم في الشهر القصير ثم يعود إلى 31. ساعة التوقيت الصيفي غير الموجودة تنتقل لأول دقيقة صالحة، والمكررة تنفذ مرة واحدة. بعد انقطاع ينفذ آخر موعد فائت فقط. التشغيل اليدوي لا يغير الموعد. الإرسال يتطلب بريد اختبار معتمدًا وإعدادات مفعلة."
              : "Weeks start Monday. Day 31 uses a shorter month's last day and returns to 31 afterward. DST gaps move to the first valid minute; repeated times execute once. Only the latest missed occurrence is caught up. Manual runs do not change the schedule. Delivery requires enabled mail settings and an approved test recipient."}
          </Alert>
        </Stack>
      </Dialog>
    </Stack>
  );
}

